import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useAuth } from 'contexts/AuthContext';
import GlassyModal from 'components/ui/GlassyModal';
import Icon from 'components/AppIcon';
import { toast } from 'sonner';

const AssignmentEditModal = ({ 
  isOpen, 
  onClose, 
  assignment, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    assignment_title: '',
    subject: '',
    assignment_type: '',
    pages: '',
    deadline: '',
    requirements: '',
    additional_notes: '',
    qa_review_notes: '',
    preferred_communication: '',
    status: '',
    assigned_writer: ''
  });
  const [writers, setWriters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingWriters, setLoadingWriters] = useState(false);
  const { user, profile } = useAuth();

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'revision-needed', label: 'Revision Needed' }
  ];

  // Communication types
  const communicationTypes = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'discord', label: 'Discord' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' }
  ];

  // Assignment types
  const assignmentTypes = [
    { value: 'essay', label: 'Essay' },
    { value: 'research-paper', label: 'Research Paper' },
    { value: 'dissertation', label: 'Dissertation' },
    { value: 'thesis', label: 'Thesis' },
    { value: 'case-study', label: 'Case Study' },
    { value: 'report', label: 'Report' },
    { value: 'presentation', label: 'Presentation' },
    { value: 'other', label: 'Other' }
  ];

  // Load writers from profiles collection
  useEffect(() => {
    const loadWriters = async () => {
      if (!isOpen) return;
      
      setLoadingWriters(true);
      try {
        const q = query(
          collection(db, 'profiles'),
          where('is_writer', '==', true)
        );
        const querySnapshot = await getDocs(q);
        const writersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setWriters(writersList);
      } catch (error) {
        console.error('Error loading writers:', error);
        toast.error('Failed to load writers');
      } finally {
        setLoadingWriters(false);
      }
    };

    loadWriters();
  }, [isOpen]);

  // Initialize form data when assignment changes
  useEffect(() => {
    if (assignment) {
      // Safe date formatting function
      const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        try {
          const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        } catch (error) {
          console.warn('Error formatting date:', dateValue, error);
          return '';
        }
      };

      setFormData({
        assignment_title: assignment.assignment_title || '',
        subject: assignment.subject || '',
        assignment_type: assignment.assignment_type || '',
        pages: assignment.pages ? assignment.pages.toString() : '',
        deadline: formatDateForInput(assignment.deadline),
        requirements: assignment.requirements || '',
        additional_notes: assignment.additional_notes || '',
        qa_review_notes: assignment.qa_review_notes || '',
        preferred_communication: assignment.preferred_communication || '',
        status: assignment.status || 'pending',
        assigned_writer: assignment.assigned_writer || ''
      });
    }
  }, [assignment]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e, shouldClose = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updates = {
        ...formData,
        pages: parseInt(formData.pages) || 0,
        deadline: formData.deadline ? new Date(formData.deadline) : null
      };

      await onSave(assignment.id, updates);
      
      // Track edit history for changed fields
      if (assignment && user && profile) {
        const changedFields = Object.keys(updates).filter(key => {
          const oldValue = assignment[key];
          const newValue = updates[key];
          return oldValue !== newValue;
        });

        for (const field of changedFields) {
          try {
            await addDoc(collection(db, 'edit_history'), {
              item_id: assignment.id,
              item_type: 'assignment',
              field: field,
              old_value: assignment[field] || '',
              new_value: updates[field] || '',
              editor_id: user.uid,
              editor_name: profile.full_name || user.email,
              editor_role: profile.is_admin ? 'admin' : profile.is_writer ? 'writer' : profile.is_qa ? 'qa' : 'user',
              created_at: serverTimestamp()
            });
          } catch (historyError) {
            console.warn('Failed to save edit history for field', field, ':', historyError);
          }
        }
      }
      
      toast.success('Assignment updated successfully');
      
      if (shouldClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error('Failed to update assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form data
    setFormData({
      assignment_title: '',
      subject: '',
      assignment_type: '',
      pages: '',
      deadline: '',
      requirements: '',
      additional_notes: '',
      qa_review_notes: '',
      preferred_communication: '',
      status: '',
      assigned_writer: ''
    });
  };

  return (
    <GlassyModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Assignment"
      icon="Edit"
      maxWidth="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Assignment Title *
            </label>
            <input
              type="text"
              name="assignment_title"
              value={formData.assignment_title}
              onChange={handleInputChange}
              required
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Enter assignment title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Subject *
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Enter subject"
            />
          </div>
        </div>

        {/* Assignment Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Assignment Type *
            </label>
            <select
              name="assignment_type"
              value={formData.assignment_type}
              onChange={handleInputChange}
              required
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select type</option>
              {assignmentTypes.map(type => (
                <option key={type.value} value={type.value} className="bg-gray-800">
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Pages *
            </label>
            <input
              type="number"
              name="pages"
              value={formData.pages}
              onChange={handleInputChange}
              required
              min="1"
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Number of pages"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Deadline
            </label>
            <input
              type="date"
              name="deadline"
              value={formData.deadline}
              onChange={handleInputChange}
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        {/* Status and Communication */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Status *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {statusOptions.map(status => (
                <option key={status.value} value={status.value} className="bg-gray-800">
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Preferred Communication
            </label>
            <select
              name="preferred_communication"
              value={formData.preferred_communication}
              onChange={handleInputChange}
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select communication type</option>
              {communicationTypes.map(type => (
                <option key={type.value} value={type.value} className="bg-gray-800">
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Writer Assignment */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Assigned Writer
          </label>
          <select
            name="assigned_writer"
            value={formData.assigned_writer}
            onChange={handleInputChange}
            disabled={loadingWriters}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
          >
            <option value="">Select writer</option>
            {writers.map(writer => (
              <option key={writer.id} value={writer.id} className="bg-gray-800">
                {writer.full_name} ({writer.email})
              </option>
            ))}
          </select>
          {loadingWriters && (
            <p className="text-sm text-white/60 mt-1">Loading writers...</p>
          )}
        </div>

        {/* Requirements */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Requirements
          </label>
          <textarea
            name="requirements"
            value={formData.requirements}
            onChange={handleInputChange}
            rows={4}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            placeholder="Enter assignment requirements"
          />
        </div>

        {/* Additional Notes */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Additional Notes
          </label>
          <textarea
            name="additional_notes"
            value={formData.additional_notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            placeholder="Any additional notes"
          />
        </div>

        {/* QA Review Notes */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            QA Review Notes
          </label>
          <textarea
            name="qa_review_notes"
            value={formData.qa_review_notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            placeholder="QA review notes and feedback"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-white/20">
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-2 text-white/80 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            disabled={loading}
            className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Icon name="Save" size={16} />
                <span>Save</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            disabled={loading}
            className="px-6 py-2 bg-primary hover:bg-primary/80 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Icon name="Save" size={16} />
                <span>Save & Close</span>
              </>
            )}
          </button>
        </div>
      </form>
    </GlassyModal>
  );
};

export default AssignmentEditModal;